import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { creatorApi, RewardsRates, LeaderboardRow } from '../services/creatorApi';

const STEPS = [
  ['Create', 'Post valuable content on SaveDelete Social — insights, finds, guides.'],
  ['Share', 'Share your posts anywhere with your personal link.'],
  ['Earn', 'Points add up from posts, engagement, shares, and referrals. Cash conversion from the monthly rewards pool is announced in advance.'],
];

const FAQ = [
  ['When do I get paid?', 'We are launching with points. Cash conversion from the monthly rewards pool (paid via PayPal) is announced in advance — your points and PayPal email carry over.'],
  ['How much can I earn?', 'Points are capped per action per day to keep things fair. The leaderboard shows what top creators earn.'],
  ['What about spam or fake accounts?', 'Fraud is detected and points are revoked; accounts get suspended. Referrals only fully pay out when the invited person genuinely participates.'],
  ['Who can join?', 'Anyone with a free SaveDelete account. Your profile is your storefront.'],
];

const RATE_ROWS: [string, string][] = [
  ['post_created', 'Publish a post'],
  ['like_received', 'Like received'],
  ['comment_received', 'Comment received'],
  ['follower_gained', 'New follower'],
  ['share_visit', 'Visitor from your share link'],
  ['referral_signup', 'Friend signs up with your link'],
  ['referral_qualified', 'Friend becomes active'],
];

const EarnLandingPage: React.FC = () => {
  const [rates, setRates] = useState<RewardsRates | null>(null);
  const [leaders, setLeaders] = useState<LeaderboardRow[]>([]);

  useEffect(() => {
    creatorApi.getRates().then(setRates).catch(() => {});
    creatorApi.getLeaderboard().then((d) => setLeaders(d.leaders)).catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>SaveDelete Creator Rewards — Create, Share, Earn Points</title>
        <meta name="description" content="Post valuable content, share it, and invite friends to earn creator points. Cash conversion from the monthly rewards pool is announced in advance. Join SaveDelete's creator rewards program free." />
        <link rel="canonical" href="https://savedelete.com/earn" />
      </Helmet>
      <main className="max-w-4xl mx-auto px-4 py-10">
        <section className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3">Create. Share. Earn.</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Earn points for posts, engagement, shares, and referrals.
            Cash conversion from the monthly rewards pool is announced in advance — your points carry over.
          </p>
          <Link to="/social" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Start earning free →</Link>
        </section>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          {STEPS.map(([title, body], i) => (
            <div key={title} className="p-5 border border-slate-200 rounded-xl">
              <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-blue-600 text-white font-bold mb-3">{i + 1}</span>
              <h2 className="font-bold text-slate-900 mb-1">{title}</h2>
              <p className="text-sm text-slate-600">{body}</p>
            </div>
          ))}
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4">How points work</h2>
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="px-4 py-2 font-semibold">Action</th><th className="px-4 py-2 font-semibold">Points</th></tr></thead>
              <tbody>
                {RATE_ROWS.map(([key, label]) => (
                  <tr key={key} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-700">{label}</td>
                    <td className="px-4 py-2 font-semibold text-slate-900">{rates ? `+${rates.points[key]}` : '…'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-2">Daily caps apply per action to keep the game fair. Fraud gets points revoked and accounts suspended.</p>
        </section>

        {leaders.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4">This month's top creators</h2>
            <ol className="space-y-2">
              {leaders.slice(0, 10).map((l, i) => (
                <li key={l.username} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-right font-bold text-slate-400">{i + 1}</span>
                  <Link to={`/social/${l.username}`} className="font-medium text-slate-900 hover:text-blue-700">{l.display_name || l.username}</Link>
                  <span className="ml-auto font-semibold text-emerald-600">{l.points} pts</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">FAQ</h2>
          <div className="space-y-4">
            {FAQ.map(([q, a]) => (
              <div key={q}>
                <h3 className="font-semibold text-slate-900">{q}</h3>
                <p className="text-sm text-slate-600 mt-1">{a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
};

export default EarnLandingPage;
